import { handleInputChange } from "./actionHandlers/textCellWithInputHandlers";
import { useTextInputHook } from "./hooks/useTextInputHook";

/**
 * Props for {@link TextCellWithInput}.
 *
 * @property value - Cell payload: `data` (current text), `id` (row id),
 *   `allowInputModification` (renders an editable input when true, otherwise a
 *   read-only label), optional `type` (cell-type identifier emitted on change),
 *   and optional `hasError` (forces a red input border when true).
 * @property onValueChangeOfTextField - Callback invoked on edit with the new
 *   value, row id, and the originating cell `type`.
 */
interface TextCellWithInputProps {
  value: {
    data: string;
    id: string;
    allowInputModification: boolean;
    type?: string;
    hasError?: boolean;
    allowEmptyValue?: boolean;
  };
  onValueChangeOfTextField: (value: string, id: string, type?: string) => void;
}

/**
 * Table cell that renders either an editable text input or a read-only
 * truncated label depending on `value.allowInputModification`. On edit it
 * forwards the new value, row id, and cell `type` via `onValueChangeOfTextField`.
 *
 * Passes `value.id` into {@link useTextInputHook} so local input state resets
 * when the cell is reused for a different row after table reorder.
 *
 * @param props - See {@link TextCellWithInputProps}.
 * @returns The rendered text cell.
 */
const TextCellWithInput: React.FC<TextCellWithInputProps> = ({
  value,
  onValueChangeOfTextField,
}) => {
  const { inputValue, setInputValue, checkIfInputIsEmpty } = useTextInputHook(
    value.data,
    value.id,
  );

  return (
    <div className="text-cell-with-input--container flex w-full items-center">
      {value.allowInputModification ? (
        <input
          type="text"
          value={inputValue}
          id={value.id}
          onChange={(e) =>
            handleInputChange(
              e,
              value.id,
              setInputValue,
              onValueChangeOfTextField,
              value.type,
            )
          }
          className={`w-full rounded-md border border-primaryGray-15 p-1 text-xs outline-none ${value.hasError || (!value.allowEmptyValue && checkIfInputIsEmpty) ? "border-red-500" : "border-primaryGray-15"}`}
        />
      ) : (
        <span className="w-full truncate text-primaryGray-1">{value.data}</span>
      )}
    </div>
  );
};

export default TextCellWithInput;
